import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { Button } from '@vodafoneis/sjonvarpskjarni-js-lib';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStepForward } from '@fortawesome/free-solid-svg-icons';
import { FADE_BLACK, FADE_BLACK_FOCUS } from '../../styles/colors';
import { PlaybackContext } from '../../contexts/PlaybackContext';
import MoviePlaylistItem from '../../models/MoviePlaylistItem';
import { breakpoints } from '../../styles/breakpoints';
import { PlaybackInformationContext } from '../../contexts/PlaybackInformationContext';

type NextEpisodeButtonContainerProps = {
	visible?: boolean;
	playbackInformationVisible?: boolean;
};

export const NextEpisodeButtonContainer = styled(Button)<NextEpisodeButtonContainerProps>`
	position: absolute;
	bottom: -55px;
	transform: ${(props) => (props.visible ? (props.playbackInformationVisible ? 'translate3d(0, -170px, 0)' : 'translate3d(0, -100px, 0)') : 'translate3d(0, 0, 0)')};
	right: 20px;
	background: ${FADE_BLACK};
	padding: 8px 12px;
	font-size: 18px;
	text-transform: uppercase;
	border-radius: 3px;
	transition: background 0.2s ease-in, transform 0.2s ease-in;
	cursor: pointer;
	z-index: 99;

	&:focus {
		outline: none;
		background-color: ${FADE_BLACK_FOCUS};
	}

	@media (hover) {
		&:hover {
			background-color: ${FADE_BLACK_FOCUS};
		}
	}

	@media ${breakpoints.md} {
		transform: ${(props) => (props.visible ? (props.playbackInformationVisible ? 'translate3d(0, -195px, 0)' : 'translate3d(0, -100px, 0)') : 'translate3d(0, 0, 0)')};
		right: 27px;
		padding: 10px 18px;
		font-size: 20px;
	}

	@media ${breakpoints.lg} {
		transform: ${(props) => (props.visible ? (props.playbackInformationVisible ? 'translate3d(0, -230px, 0)' : 'translate3d(0, -110px, 0)') : 'translate3d(0, 0, 0)')};
		right: 50px;
		padding: 14px 24px;
		font-size: 22px;
	}
`;

export const NextEpisodeButtonLabel = styled.span`
	margin-right: 10px;
`;

export const NextEpisodeIcon = styled(FontAwesomeIcon)`
	padding: 2px 0;
`;

export const NextEpisodeButton: React.FC = () => {
	const { t } = useTranslation();
	const { position, duration, playbackInformationVisible } = useContext(PlaybackInformationContext);
	const { playlistItem, hasNext, next } = useContext(PlaybackContext);

	const creditTiming = useMemo(() => {
		if (playlistItem instanceof MoviePlaylistItem && playlistItem.movie.creditTiming) {
			return playlistItem.movie.creditTiming;
		}

		return null;
	}, [playlistItem]);

	const visible = useMemo(() => {
		if (!hasNext) return false;

		if (creditTiming && position > creditTiming.start) return true;

		return duration > 30 && position > duration - 30;
	}, [creditTiming, duration, hasNext, position]);

	return (
		<NextEpisodeButtonContainer visible={visible} playbackInformationVisible={playbackInformationVisible} onClick={next}>
			<NextEpisodeButtonLabel>{t('Player.NextEpisode')}</NextEpisodeButtonLabel>
			<NextEpisodeIcon icon={faStepForward} />
		</NextEpisodeButtonContainer>
	);
};
